import React, { useState, useEffect } from "react";
import {
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Button,
  Modal,
  IconButton,
} from "@mui/material";
import { Assignment as AssignmentIcon, Visibility as VisibilityIcon, Close as CloseIcon } from "@mui/icons-material";

const StudentHomework = () => {
  const [homeworks, setHomeworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedHomework, setSelectedHomework] = useState(null);

  useEffect(() => {
    const fetchHomeworkList = async () => {
      try {
        const token = sessionStorage.getItem("token");
        const classId = sessionStorage.getItem("classId");
        const sectionId = sessionStorage.getItem("sectionId");

        if (!token || !classId || !sectionId) {
          throw new Error("Missing authentication or class details.");
        }

        const response = await fetch(`${process.env.REACT_APP_BASE_URL}/Homework/ListByClass`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
          body: JSON.stringify({ classId, sectionId }),
        });

        if (!response.ok) {
          throw new Error("Failed to fetch homework list");
        }

        const responseData = await response.json();

        if (!responseData || !responseData.homework) {
          setHomeworks([]);
        } else {
          setHomeworks(Array.isArray(responseData.homework) ? responseData.homework : []);
        }
      } catch (err) {
        console.error("Error fetching homework:", err);
        setError("Unable to load homework. Please try again later.");
        setHomeworks([]);
      } finally {
        setLoading(false);
      }
    };

    fetchHomeworkList();
  }, []);

  const handleView = (homework) => {
    setSelectedHomework(homework);
    setShowViewModal(true);
  };

  const handleClose = () => {
    setShowViewModal(false);
  };

  // Loading State
  if (loading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="70vh"
        textAlign="center"
      >
        <CircularProgress size={60} thickness={5} sx={{ mb: 2 }} />
        <Typography variant="h6" color="textPrimary">
          Loading homework...
        </Typography>
      </Box>
    );
  }

  // Error State
  if (error) {
    return (
      <Box textAlign="center" mt={8} px={3}>
        <Typography variant="h6" color="error" gutterBottom>
          Oops! Something went wrong.
        </Typography>
        <Typography variant="body1" color="textSecondary">
          {error}
        </Typography>
      </Box>
    );
  }

  // Empty State
  if (homeworks.length === 0) {
    return (
      <Container maxWidth="md">
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight="70vh"
          textAlign="center"
          px={3}
        >
          <AssignmentIcon sx={{ fontSize: 120, color: "#bbdefb", mb: 4 }} />
          <Typography
            variant="h5"
            gutterBottom
            color="textPrimary"
            fontWeight="medium"
          >
            No Homework Available
          </Typography>
          <Typography
            variant="body1"
            color="textSecondary"
            maxWidth="600px"
            mx="auto"
            paragraph
          >
            There is currently no homework assigned for your class and section.
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Please contact your school administration or check back later for updates.
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <>
      <Typography
        variant="h4"
        gutterBottom
        align="center"
        fontWeight="bold"
        color="#333"
        mb={3}
      >
        Homework
      </Typography>

      <TableContainer component={Paper} elevation={6} sx={{ borderRadius: 2, overflow: "hidden", mb: 4 }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  background: 'linear-gradient(111.1deg, rgba(69,150,164,1) 2.5%, rgba(17,20,34,1) 100.3%)',
                  color: "white",
                  fontWeight: "bold",
                  fontSize: "1.1rem",
                  py: 2,
                  textAlign: "center"
                }}
              >
                S.No
              </TableCell>
              <TableCell
                sx={{
                  background: 'linear-gradient(111.1deg, rgba(69,150,164,1) 2.5%, rgba(17,20,34,1) 100.3%)',
                  color: "white",
                  fontWeight: "bold",
                  fontSize: "1.1rem",
                  py: 2,
                }}
              >
                Description
              </TableCell>
              <TableCell
                sx={{
                  background: 'linear-gradient(111.1deg, rgba(69,150,164,1) 2.5%, rgba(17,20,34,1) 100.3%)',
                  color: "white",
                  fontWeight: "bold",
                  fontSize: "1.1rem",
                  py: 2,
                }}
              >
                Homework Date
              </TableCell>
              <TableCell
                sx={{
                  background: 'linear-gradient(111.1deg, rgba(69,150,164,1) 2.5%, rgba(17,20,34,1) 100.3%)',
                  color: "white",
                  fontWeight: "bold",
                  fontSize: "1.1rem",
                  py: 2,
                }}
              >
                Submission Date
              </TableCell>
              <TableCell
                sx={{
                  background: 'linear-gradient(111.1deg, rgba(69,150,164,1) 2.5%, rgba(17,20,34,1) 100.3%)',
                  color: "white",
                  fontWeight: "bold",
                  fontSize: "1.1rem",
                  py: 2,
                  textAlign: "center"
                }}
              >
                Action
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {homeworks.map((homework, index) => (
              <TableRow
                key={homework.homeworkId}
                hover
                sx={{ "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" } }}
              >
                <TableCell sx={{ fontWeight: "bold", borderRight: "1px solid #e0e0e0", textAlign: "center" }}>
                  {index + 1}
                </TableCell>
                <TableCell sx={{ borderRight: "1px solid #e0e0e0" }}>
                  {homework.caption}
                </TableCell>
                <TableCell sx={{ borderRight: "1px solid #e0e0e0", textAlign: "center" }}>
                  {new Date(homework.homeworkDate).toLocaleDateString()}
                </TableCell>
                <TableCell sx={{ borderRight: "1px solid #e0e0e0", textAlign: "center" }}>
                  {new Date(homework.dueDate).toLocaleDateString()}
                </TableCell>
                <TableCell sx={{ textAlign: "center" }}>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<VisibilityIcon />}
                    onClick={() => handleView(homework)}
                    sx={{
                      background: 'linear-gradient(111.1deg, rgba(69,150,164,1) 2.5%, rgba(17,20,34,1) 100.3%)',
                      color: "white",
                      fontWeight: "bold",
                      "&:hover": {
                        background: 'linear-gradient(111.1deg, rgba(69,150,164,0.8) 2.5%, rgba(17,20,34,0.8) 100.3%)',
                      }
                    }}
                  >
                    View
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* View Homework Modal */}
      <Modal open={showViewModal} onClose={handleClose}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "95%", sm: 600 },
            maxHeight: "80vh",
            bgcolor: "background.paper",
            borderRadius: 3,
            boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              background: 'linear-gradient(111.1deg, rgba(69,150,164,1) 2.5%, rgba(17,20,34,1) 100.3%)',
              padding: "16px 20px",
              display: "flex",
              alignItems: "center",
              gap: 1,
              position: "relative",
              color: "white",
            }}
          >
            <IconButton
              onClick={handleClose}
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                color: "#fff",
              }}
            >
              <CloseIcon />
            </IconButton>
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: "1.1rem" }}>
              View Homework
            </Typography>
          </Box>
          <Box sx={{ padding: "24px 20px", overflowX: "hidden", overflowY: "auto" }}>
            <Typography sx={{ mb: 3, fontSize: "1rem", lineHeight: 1.6 }}>
              <strong>Description:</strong> {selectedHomework?.caption}
            </Typography>
           
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, maxHeight: "300px", overflow: "auto" }}>
              {selectedHomework?.files?.length > 0 ? (
                selectedHomework.files.map((file) => (
                 <Box
  key={file.fileId}
  sx={{
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    p: 1.5,
    backgroundColor: "#f0f9ff",
    borderRadius: 1,
  }}
>
  <Button
    variant="contained"
    onClick={() =>
      window.open(
        `${process.env.REACT_APP_DOCUMENT_URL}${file.filePath}`,
        "_blank"
      )
    }
    sx={{
      fontWeight: 600,
      textTransform: "none",
      backgroundColor: "#459ec4",
      color: "#fff",
      borderRadius: 1,
      padding: "8px 16px",
      boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
      "&:hover": {
        backgroundColor: "#3a8bbb",
        boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
      },
    }}
  >
    {file.originalFileName}
  </Button>
</Box>

                ))
              ) : (
                <Typography variant="body2" sx={{ color: "#666" }}>
                  No files attached.
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default StudentHomework;
